import { Document } from "~/document";
import { Checkbox } from "~/components/ui/checkbox";
import { observer } from "mobx-react";
import { documentDownloadLink, docFileSize, FileExtension } from "~/document";
import { Download, File, FileImage, FileSpreadsheet, FileText } from "lucide-react";
import { FileExcelIcon, FilePdfIcon, FilePptIcon, FileWordIcon } from "~/components/icons/FileTypeIcons";
import cn from "classnames";
import bulkDownloader from "~/stores/bulkDownloader";
import { ComponentType, useMemo } from "react";
import moment from "moment";
import { DOC_PUB_DATE_FORMAT } from "~/document";
import mime from "mime-types";
import { getFullDocTypes } from "~/util";
import documentStore from "~/stores/document";

type FileIcon = ComponentType<{ className?: string }>;

const fileTypeToIconMapping: Partial<Record<FileExtension, FileIcon>> = {
  ".doc": FileWordIcon,
  ".docx": FileWordIcon,
  ".xls": FileExcelIcon,
  ".xlsx": FileExcelIcon,
  ".csv": FileSpreadsheet,
  ".odt": FilePptIcon,
  ".pdf": FilePdfIcon,
  ".jpg": FileImage,
  ".jpeg": FileImage,
  ".png": FileImage,
  ".ppt": FilePptIcon,
  ".pptx": FilePptIcon,
  ".txt": FileText,
  ".md": FileText,
};

const getFileExt = (doc: Document): string | null => {
  if (!doc.file_info) return null;
  if (doc.file_info.ext && doc.file_info.ext.length !== 0) {
    return doc.file_info.ext;
  }
  if (doc.file_info.mimetype) {
    const e = mime.extension(doc.file_info.mimetype);
    if (e) return e;
  }
  return null;
};

const DocIcon = (props: { doc: Document }) => {
  const { doc } = props;
  const Icon: FileIcon = useMemo(() => {
    const ext = getFileExt(doc);
    const mapped = ext ? fileTypeToIconMapping[ext as FileExtension] : undefined;
    return mapped ?? File;
  }, [doc.file_info?.ext]);
  const fsz = docFileSize(doc);
  const languagesRendered = useMemo(() => {
    let res: string[] = [doc.language.toUpperCase()];
    if (doc.translations) {
      for (let lang of doc.translations) {
        if (lang[0]) {
          res.push(lang[0].toUpperCase());
        }
      }
    }
    return res.length >= 4 ? res.slice(0, 4).join("/") + "..." : res.join("/");
  }, [doc.language_display, doc.translations]);
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex h-20 w-[70px] flex-col items-center justify-center gap-1 bg-[#eff2f5] px-1 text-[10px] [&_svg]:h-[34px] [&_svg]:w-auto">
        <Icon />
        {fsz && <strong>{fsz}</strong>}
      </div>
      <div className="mt-3 text-xs font-bold">{languagesRendered}</div>
    </div>
  );
};

const infoItems = Object.entries({
  type_display: "Document types",
  countries_display: "Geographical coverage",
  protocol: "Document ID",
  number: "Document Symbol",
});

type Props = { doc: Document; onDocumentClick: () => void; className?: string };

const DocItem = observer((props: Props) => {
  const doc = props.doc as Document;

  const subContentItems = infoItems.filter(([key]) => !!doc[key]);
  const bulkDownloadSelected = bulkDownloader.isBulkDownloadSelected(doc.protocol);

  const expired = documentStore.isExpired(doc);
  const archived = documentStore.isArchived(doc);
  const canDownload = documentStore.canDownload(doc);

  return (
    <div className="relative my-4 flex cursor-default flex-col overflow-hidden">
      {expired && (
        <div className="absolute -left-[30px] top-3 z-[1] -rotate-45 bg-wfp-red px-[27px] py-2.5 text-sm font-semibold text-white">
          Expired
        </div>
      )}
      {archived && (
        <div className="absolute -left-[30px] top-3 z-[1] -rotate-45 bg-wfp-red px-[27px] py-2.5 text-sm font-semibold text-white">
          Archived
        </div>
      )}
      <div className={cn("flex gap-4 border-b border-[var(--ui-03)] pb-4", props.className)}>
        {/* file icon / size / language */}
        <div className="shrink-0">
          <DocIcon doc={doc} />
        </div>

        {/* main content */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* title row */}
          <div className="flex items-center">
            <div className="mb-0.5 min-w-0">
              <a
                title="Open document details"
                className="mr-2 cursor-pointer font-semibold text-foreground hover:underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  props.onDocumentClick();
                }}
              >
                {doc.title}
              </a>
              {canDownload && (
                <a
                  className="inline-block align-middle text-xs font-bold text-primary-600 hover:text-primary-700"
                  href={documentDownloadLink(doc)}
                  target="blank_"
                  title="Download document"
                >
                  <Download className="inline size-3.5" />
                </a>
              )}
            </div>
          </div>

          {/* text + sub-content + hint */}
          <div className="flex flex-1 items-stretch justify-between gap-4 max-[720px]:flex-col max-[720px]:items-start">
            <div className="min-w-0 flex-1 text-sm leading-normal">
              <div className="mb-1.5">{doc.topics_display}</div>
              <div className="flex flex-col justify-between [color:var(--text-02)]">
                <div>
                  {doc.abstract?.length !== 0 && <div className="mb-2">{doc.abstract}</div>}
                  <div className="mb-3">
                    {subContentItems.map(([key, title]) => (
                      <div className="mb-1 text-xs last:mb-0" key={key}>
                        <span className="font-semibold">{title}: </span>
                        <span>{key == "type_display" ? getFullDocTypes(doc) : doc[key]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {canDownload && (
                  <div className="select-none flex items-center gap-2">
                    <Checkbox
                      id={`doc-item-bd-${doc.protocol}`}
                      title="Bulk download"
                      disabled={!canDownload}
                      checked={bulkDownloadSelected}
                      onCheckedChange={(checked) => {
                        bulkDownloader.setBulkDownloadSelected(doc, checked === true);
                      }}
                    />
                    <label htmlFor={`doc-item-bd-${doc.protocol}`} className="cursor-pointer text-sm">
                      Add to bulk download
                    </label>
                  </div>
                )}
              </div>
            </div>

            {doc.publication_date && (
              <div className="ml-4 flex flex-col justify-end text-right text-xs max-[720px]:ml-0 max-[720px]:text-left">
                <div className="mb-1 [color:var(--text-02)]">published on</div>
                <div>{moment(new Date(doc.publication_date)).format(DOC_PUB_DATE_FORMAT)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default DocItem;
